import type React from 'react';
import { WordsHappenButton, type WordsHappenButtonVariant } from '../button';
import { type AuthProvider, DEFAULT_AUTH_PROVIDERS } from './auth-providers';
import { AppleGlyph, EmailGlyph, GoogleGlyph, MicrosoftGlyph } from './provider-icons';
import styles from './social-auth-row.module.scss';

interface ProviderConfig {
  name: string;
  Glyph: React.FC<{ size: number }>;
  variant: Extract<WordsHappenButtonVariant, 'ink' | 'paper'>;
}

/** Everything a provider button needs: its name, brand glyph and button fill. */
export const AUTH_PROVIDERS: Record<AuthProvider, ProviderConfig> = {
  apple: { name: 'Apple', Glyph: AppleGlyph, variant: 'ink' },
  google: { name: 'Google', Glyph: GoogleGlyph, variant: 'paper' },
  microsoft: { name: 'Microsoft', Glyph: MicrosoftGlyph, variant: 'paper' },
  email: { name: 'Email', Glyph: EmailGlyph, variant: 'paper' },
};

export interface SocialAuthRowProps {
  providers?: readonly AuthProvider[];
  /**
   * The providers that can be used. The others in `providers` are shown disabled and named "Continue with
   * {provider} (coming soon)". Every provider is enabled when it is left out.
   */
  enabledProviders?: readonly AuthProvider[];
  /** A provider whose sign-in is starting: its button shows a spinner and every button is disabled. */
  busyProvider?: AuthProvider;
  /** Icon-only buttons side by side. Otherwise, full-width labelled buttons stacked. */
  compact?: boolean;
  onSelect: (provider: AuthProvider) => void;
  className?: string;
}

/** A button's accessible name: "Continue with {provider}", with "(coming soon)" when it can't be used yet. */
const continueWith = (provider: AuthProvider, comingSoon = false) =>
  `Continue with ${AUTH_PROVIDERS[provider].name}${comingSoon ? ' (coming soon)' : ''}`;

/** One button per sign-in provider, each named "Continue with {provider}". */
export const SocialAuthRow: React.FC<SocialAuthRowProps> = ({
  providers = DEFAULT_AUTH_PROVIDERS,
  enabledProviders,
  busyProvider,
  compact = false,
  onSelect,
  className,
}) => {
  const cls = [styles.row, compact ? styles.compact : styles.stacked, className].filter(Boolean).join(' ');

  return (
    <div className={cls}>
      {providers.map((provider) => {
        const { Glyph, variant } = AUTH_PROVIDERS[provider];
        const comingSoon = enabledProviders !== undefined && !enabledProviders.includes(provider);
        const name = continueWith(provider, comingSoon);
        return (
          <WordsHappenButton
            key={provider}
            variant={variant}
            className={compact ? styles.iconButton : styles.labelledButton}
            aria-label={compact ? name : undefined}
            title={comingSoon && compact ? name : undefined}
            icon={<Glyph size={compact ? 16 : 18} />}
            disabled={comingSoon || (busyProvider !== undefined && busyProvider !== provider)}
            loading={busyProvider === provider}
            onClick={() => onSelect(provider)}
          >
            {compact ? null : name}
          </WordsHappenButton>
        );
      })}
    </div>
  );
};
